import React, { useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { Box } from '@mui/material';

const ThemeTransition = ({ active, direction = 'light-to-dark' }) => {
    const canvasRef = useRef(null);
    const containerRef = useRef(null);

    useEffect(() => {
        if (!active) return;

        // Check for reduced motion preference
        const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        if (prefersReducedMotion) {
            // Skip animation entirely for accessibility
            if (containerRef.current) {
                containerRef.current.style.opacity = 0;
            }
            return;
        }

        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');
        let animationFrameId;
        let stars = [];
        
        // Dynamic star count based on screen size
        const width = window.innerWidth;
        const height = window.innerHeight;
        const numStars = width > 1920 ? 400 : width > 1280 ? 250 : 150;
        
        let speed = 2; // Starting speed
        let opacity = 0; // Container opacity
        let fadeStage = 'in'; // 'in', 'warp', 'out'

        canvas.width = width;
        canvas.height = height;

        // Initialize stars
        // We create stars that radiate from center
        for (let i = 0; i < numStars; i++) {
            stars.push({
                x: Math.random() * width - width / 2,
                y: Math.random() * height - height / 2,
                z: Math.random() * width // depth
            });
        }

        const render = () => {
            // Fill background based on phase
            // Dark mode target -> fade to black
            // Light mode target -> fade to white
            const baseColor = direction === 'light-to-dark' ? 0 : 255;

            // Clear / Trail effect
            ctx.fillStyle = `rgba(${baseColor}, ${baseColor}, ${baseColor}, 0.3)`;
            ctx.fillRect(0, 0, width, height);

            // Logic for phases
            if (fadeStage === 'in') {
                opacity += 0.08; // Faster fade-in
                if (opacity >= 1) {
                    opacity = 1;
                    fadeStage = 'warp';
                }
            } else if (fadeStage === 'warp') {
                speed *= 1.08; // Faster acceleration
                if (speed > 80) { // Reach max warp sooner
                    fadeStage = 'out';
                    // Animation purely visual now, controlled by parent timeout
                }
            } else if (fadeStage === 'out') {
                // The theme has technically switched behind the scene
                // Now we fade out the canvas to reveal it
                opacity -= 0.08; // Faster fade-out
                if (opacity <= 0) {
                    opacity = 0;
                    cancelAnimationFrame(animationFrameId);
                    return; // End animation
                }
            }

            // Apply opacity to the entire container if possible, 
            // but easier to control strictly in canvas rendering or CSS
            if (containerRef.current) {
                containerRef.current.style.opacity = opacity;
            }

            // Move Stars
            const cx = width / 2;
            const cy = height / 2;

            stars.forEach(star => {
                // Move star closer
                star.z -= speed;

                // Reset star if it passes camera
                if (star.z <= 0) {
                    star.z = width;
                    star.x = Math.random() * width - width / 2;
                    star.y = Math.random() * height - height / 2;
                }

                // Project star
                const x = (star.x / star.z) * width + cx;
                const y = (star.y / star.z) * height + cy;

                // Calculate size and color based on depth/speed
                const size = (1 - star.z / width) * (speed / 2);
                const colorVal = direction === 'light-to-dark' ? 255 : 0; // Stars contrast background

                ctx.beginPath();
                ctx.fillStyle = `rgba(${colorVal},${colorVal},${colorVal}, ${1 - star.z / width})`;
                ctx.arc(x, y, size > 0 ? size : 0, 0, Math.PI * 2);
                ctx.fill();

                // Draw line trail at high speeds
                if (speed > 20) {
                    ctx.beginPath();
                    ctx.strokeStyle = `rgba(${colorVal},${colorVal},${colorVal}, 0.5)`;
                    ctx.lineWidth = size;
                    ctx.moveTo(x, y);
                    // Previous position estimate
                    const prevZ = star.z + speed;
                    const prevX = (star.x / prevZ) * width + cx;
                    const prevY = (star.y / prevZ) * height + cy;
                    ctx.lineTo(prevX, prevY);
                    ctx.stroke();
                }
            });

            animationFrameId = requestAnimationFrame(render);
        };

        render();

        return () => {
            cancelAnimationFrame(animationFrameId);
        };
    }, [active, direction]); // Removed onComplete dependency

    if (!active) return null;

    return ReactDOM.createPortal(
        <Box
            ref={containerRef}
            sx={{
                position: 'fixed',
                top: 0,
                left: 0,
                width: '100vw',
                height: '100vh',
                zIndex: 9999,
                pointerEvents: 'none',
                opacity: 0, // Starts invisible
                transition: 'opacity 0.2s ease-in-out',
                bgcolor: direction === 'light-to-dark' ? '#000' : '#FFF' // Fallback bg
            }}
        >
            <canvas ref={canvasRef} style={{ display: 'block' }} />
        </Box>,
        document.body
    );
};

export default ThemeTransition;
